import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Building2,
  Hash,
  MapPin,
  Pencil,
  ReceiptText,
  RefreshCw,
  UserRound
} from 'lucide-react';
import type {
  AccountListItem,
  CrmContact,
  ShowHubspotCompanyOverviewResponse,
  VatIdValidation
} from '@bliks-io/contracts';
import { Badge, Button, Card } from '@bliks-io/ui';
import { OpenInSystemButton } from './OpenInSystemButton';

/**
 * Die Firma im CRM, an der dieses Konto hängt.
 *
 * Eine eigene Karte, weil die Verknüpfung eine Sache für sich ist: sie sagt,
 * woher Adresse, USt-IdNr. und Rechnungsempfänger kommen (LIC-26), und sie
 * ist der Grund, warum ein Angebot überhaupt im CRM landen kann.
 *
 * Der Knopf zum Verknüpfen sitzt **hier** und nicht mehr oben rechts in der
 * Kopfzeile. Dort stand er neben dem Kontonamen, ohne zu sagen, worauf er
 * sich bezieht — eine Handlung gehört an das, was sie ändert.
 *
 * Das Logo kommt aus HubSpot und wird nicht kopiert: es ändert sich, wenn die
 * Firma ihre Website wechselt, und eine Kopie bei uns wäre die veraltete
 * Fassung davon. Welches der beiden HubSpot-Logos gilt, entscheidet der
 * Server — siehe `logoUrl` im Vertrag.
 */
export function HubspotCompanyCard({
  companyId,
  overview,
  primaryContact,
  billingContact,
  masterData,
  onEdit,
  onRefresh,
  isRefreshing
}: {
  /** `null` und `undefined` bedeuten dasselbe: noch keine Firma verknuepft. */
  companyId: string | null | undefined;
  overview: ShowHubspotCompanyOverviewResponse | undefined;
  /** Der Hauptansprechpartner aus dem CRM; `null`, wo keiner gekennzeichnet ist. */
  primaryContact: CrmContact | null | undefined;
  /** Der Abrechnungskontakt aus dem CRM; `null`, wo keiner gekennzeichnet ist. */
  billingContact: CrmContact | null | undefined;
  /**
   * Anschrift, USt-IdNr. und Rechnungsempfänger — die drei Felder, die das
   * CRM führt und aus denen der Anbieter seine Rechnung baut.
   */
  masterData: AccountListItem | null | undefined;
  onEdit: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <CompanyMark logoUrl={overview?.logoUrl ?? null} name={overview?.name ?? null} />
          <div className="min-w-0">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('accountAdmin.company.heading')}
            </p>
            {companyId ? (
              <>
                <p className="truncate font-medium">
                  {overview?.name ?? t('accountAdmin.company.unknownName')}
                </p>
                <p className="text-muted-foreground truncate text-sm">{overview?.domain ?? '—'}</p>
                {/* Die Id, weil sie im CRM gesucht und in Fehlermeldungen
                    genannt wird — ohne sie ist „die Firma" eine Behauptung. */}
                <p className="text-muted-foreground mt-1 font-mono text-xs">{companyId}</p>
                <ContactLine
                  contact={primaryContact ?? null}
                  icon={UserRound}
                  role={t('accountAdmin.company.primaryContactRole')}
                  missing={t('accountAdmin.company.noContact')}
                />
                <ContactLine
                  contact={billingContact ?? null}
                  icon={ReceiptText}
                  role={t('accountAdmin.company.billingContactRole')}
                  missing={t('accountAdmin.company.noBillingContact')}
                />
                <MasterData account={masterData ?? null} />
              </>
            ) : (
              <p className="text-muted-foreground text-sm">{t('accountAdmin.company.unlinked')}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          {/* Nur wo es eine Firma gibt: ohne sie gäbe es nichts zu lesen,
              und ein Knopf, der nie etwas tun kann, ist keiner. */}
          {companyId && (
            <Button variant="outline" size="small" disabled={isRefreshing} onClick={onRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('accountAdmin.company.refresh')}
            </Button>
          )}
          <OpenInSystemButton url={overview?.companyUrl} label="HubSpot" />
          <Button variant="outline" size="small" onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            {companyId ? t('accountAdmin.changeCompany') : t('accountAdmin.assignCompany')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Das Logo, oder ein Platzhalter. Eine Lücke, wo bei den anderen ein Bild
 * steht, liest sich als Ladefehler — nicht als Firma ohne Logo.
 */
function CompanyMark({ logoUrl, name }: { logoUrl: string | null; name: string | null }) {
  if (!logoUrl) {
    return (
      <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
        <Building2 className="text-muted-foreground h-5 w-5" />
      </div>
    );
  }
  return (
    <img src={logoUrl} alt={name ?? ''} className="h-10 w-10 shrink-0 rounded-md object-contain" />
  );
}

/**
 * Wer bei dieser Firma angeschrieben wird — einmal für das Angebot, einmal
 * für die Rechnung.
 *
 * Beide stehen hier und nicht bei den Rechnungsdaten: dort steht, **wohin**
 * eine Rechnung geht (`billingEmail`), hier **wer** sie erwartet. Das sind
 * zwei Angaben, und sie dürfen auseinandergehen — aufgelöst wird das im CRM,
 * wo alle Stammdaten gepflegt werden (LIC-26).
 */
function ContactLine({
  contact,
  icon: Icon,
  role,
  missing
}: {
  contact: CrmContact | null;
  icon: typeof UserRound;
  role: string;
  missing: string;
}) {
  const { t } = useTranslation();
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');

  return (
    <p className="text-muted-foreground mt-2 flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 shrink-0" />
      {contact ? (
        <span className="truncate">
          {name || t('accountAdmin.company.contactUnnamed')}
          {contact.email && ` · ${contact.email}`}
          <span className="ml-1 text-xs">({role})</span>
        </span>
      ) : (
        <span className="truncate">{missing}</span>
      )}
    </p>
  );
}

/**
 * Die Stammdaten, die aus dieser Firma gelesen wurden.
 *
 * Nur lesend und ohne Stift: geändert werden sie im CRM (LIC-26). Sie
 * stehen trotzdem hier, weil sich an ihnen entscheidet, was auf der Rechnung
 * steht — ohne Anschrift rechnet der Anbieter nach seinem eigenen Sitz ab,
 * und ohne bestätigte USt-IdNr. gibt es kein Reverse Charge.
 */
function MasterData({ account }: { account: AccountListItem | null }) {
  const { t } = useTranslation();
  if (!account) return null;

  const address = account.billingAddress;
  const validation = account.vatIdValidation;

  return (
    <div className="mt-3 space-y-1 border-t pt-3">
      <DataLine icon={MapPin} missing={!address}>
        {address
          ? [address.line1, address.line2, `${address.postalCode} ${address.city}`, address.country]
              .filter(Boolean)
              .join(', ')
          : t('accountAdmin.company.noAddress')}
      </DataLine>
      <DataLine icon={Hash} missing={!account.vatId}>
        {account.vatId ? (
          <>
            {account.vatId}
            {/* Eine ungeprüfte Nummer sieht aus wie eine bestätigte — nur
                die bestätigte geht an den Anbieter und trägt das Reverse
                Charge. */}
            <Badge className="ml-2" colorScheme={vatState(validation).tone}>
              {t(vatState(validation).key)}
            </Badge>
          </>
        ) : (
          t('accountAdmin.company.noVatId')
        )}
      </DataLine>
      <DataLine icon={ReceiptText} missing={!account.billingEmail}>
        {account.billingEmail ?? t('accountAdmin.company.noBillingEmail')}
      </DataLine>
    </div>
  );
}

/**
 * Wie eine USt-IdNr. dasteht: bestätigt, abgelehnt oder noch ungeprüft.
 *
 * „Noch nie gefragt" ist nicht „abgelehnt" — nur die bestätigte Nummer geht
 * an den Anbieter und trägt das Reverse Charge, und die ungeprüfte sähe sonst
 * aus wie ein Fehler des Kunden.
 */
function vatState(validation: VatIdValidation | null): {
  tone: 'success' | 'error' | 'neutral';
  key: string;
} {
  if (!validation) return { tone: 'neutral', key: 'accountAdmin.company.vatUnchecked' };
  return validation.valid
    ? { tone: 'success', key: 'accountAdmin.company.vatConfirmed' }
    : { tone: 'error', key: 'accountAdmin.company.vatRejected' };
}

/** Eine Zeile Stammdaten; fehlt der Wert, sagt das Zeichen davor es mit. */
function DataLine({
  icon: Icon,
  missing,
  children
}: {
  icon: typeof MapPin;
  missing: boolean;
  children: React.ReactNode;
}) {
  return (
    <p
      className={`flex items-center gap-2 text-sm ${missing ? 'text-destructive' : 'text-muted-foreground'}`}
    >
      {missing ? (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      ) : (
        <Icon className="h-4 w-4 shrink-0" />
      )}
      <span className="truncate">{children}</span>
    </p>
  );
}
